// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — a viewer's colours for each dataset follow a signed-in account,
 * as playlists did until they retired for Khipus (owner, 2026-10-09: "sync like playlists").
 *
 * The browser stays where they live (`displayPrefs.ts`, localStorage);
 * this keeps a copy on the account (`/api/account/display`) and the two
 * in step, the way playlists were until 2026-11-03:
 *   - on sign-in, on page load while signed in, and when the page comes
 *     back into view after a minute away, the account's copy is fetched
 *     and merged in (`displayPrefsMerge.ts`: newest entry per dataset,
 *     resets included);
 *   - after any change here, the set is sent (1.5 s after the last
 *     change) and the server's merge comes back and is applied.
 * Signed out, nothing is sent and the colours stay in this browser. A
 * failed request leaves them as they are here, to be sent next time.
 */

import { accountState, onAccountChange, type AccountState } from './account'
import { applyDisplayPrefsDoc, exportDisplayPrefsDoc, onDisplayPrefsChange } from './displayPrefs'
import { mergeDisplayPrefsDocs, sameDisplayPrefsDoc, sanitizeDisplayPrefsDoc, type DisplayPrefsDoc } from './displayPrefsMerge'

const ENDPOINT = '/api/account/display'
const PUSH_DELAY_MS = 1500
const REFRESH_AFTER_MS = 60_000

let account: string | null = null
/** The set as last agreed with the account; a change that leaves it equal is not sent. */
let agreed: DisplayPrefsDoc | null = null
let pushTimer: ReturnType<typeof setTimeout> | null = null
let lastPullAt = 0
let started = false
let fetchImpl: typeof fetch = (input, init) => fetch(input, init)

async function request(method: 'GET' | 'PUT', body?: DisplayPrefsDoc): Promise<DisplayPrefsDoc | null> {
  try {
    const res = await fetchImpl(ENDPOINT, {
      method,
      headers: body ? { 'Content-Type': 'application/json', Accept: 'application/json' } : { Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
      credentials: 'same-origin',
    })
    if (!res.ok) return null
    return sanitizeDisplayPrefsDoc(await res.json())
  } catch {
    return null
  }
}

/** Apply a set from the account here, unless it is what this browser already has. */
function adopt(doc: DisplayPrefsDoc): void {
  agreed = doc
  if (!sameDisplayPrefsDoc(doc, exportDisplayPrefsDoc())) applyDisplayPrefsDoc(doc)
}

/** Fetch the account's copy, merge it with this browser's, and send the merge back if the account lacked something. */
async function pull(): Promise<void> {
  const forAccount = account
  if (!forAccount) return
  lastPullAt = Date.now()
  const remote = await request('GET')
  if (account !== forAccount || !remote) return
  const merged = mergeDisplayPrefsDocs(exportDisplayPrefsDoc(), remote, Date.now())
  // Agreed first, so applying the merge here does not schedule a second send.
  agreed = merged
  if (!sameDisplayPrefsDoc(merged, exportDisplayPrefsDoc())) applyDisplayPrefsDoc(merged)
  if (!sameDisplayPrefsDoc(merged, remote)) await push()
}

/** Send this browser's set; the server merges and answers with the result. */
async function push(): Promise<void> {
  const forAccount = account
  if (!forAccount) return
  const reply = await request('PUT', exportDisplayPrefsDoc())
  if (account !== forAccount || !reply) return
  adopt(reply)
}

function schedulePush(): void {
  if (!account) return
  if (agreed && sameDisplayPrefsDoc(agreed, exportDisplayPrefsDoc())) return
  if (pushTimer) clearTimeout(pushTimer)
  pushTimer = setTimeout(() => {
    pushTimer = null
    void push()
  }, PUSH_DELAY_MS)
}

function onAccount(state: AccountState): void {
  const next = state.status === 'signedIn' ? state.email : null
  if (state.status === 'unknown' || next === account) return
  account = next
  agreed = null
  if (pushTimer) {
    clearTimeout(pushTimer)
    pushTimer = null
  }
  if (account) void pull()
}

/** Start keeping colours in step with the signed-in account. Idempotent. */
export function startDisplayPrefsSync(opts: { fetchImpl?: typeof fetch } = {}): void {
  if (opts.fetchImpl) fetchImpl = opts.fetchImpl
  if (started) return
  started = true
  onDisplayPrefsChange(schedulePush)
  onAccountChange(onAccount)
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && account && Date.now() - lastPullAt > REFRESH_AFTER_MS) void pull()
    })
  }
  onAccount(accountState())
}

/** Tests only: forget the account and timers. */
export function resetDisplayPrefsSyncForTests(): void {
  account = null
  agreed = null
  lastPullAt = 0
  if (pushTimer) clearTimeout(pushTimer)
  pushTimer = null
}
